import React, { FC, useEffect, useRef, useState } from 'react';
import type { HandbookImport } from '@dreamai/types';
import type {
  HandbookDocument,
  HandbookPageFill,
  HandbookPrefillResults,
  HandbookPreviewResponse,
  HandbookPreviewSource,
  HandbookSetup,
  HandbookSetupAnswers,
  HandbookSetupOwnSection,
  HandbookSetupTopicId,
  SaveHandbookSetupRequest,
} from './handbookContract';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import { ReactComponent as ClockIcon } from '../../icons/clock.svg';
import { ReactComponent as ChatIcon } from '../../icons/chat-double-bubble.svg';
import { ReactComponent as BookOpenIcon } from '../../icons/book-open-01.svg';
import OrganisationModal from './OrganisationModal';
import { HandbookUploadField, uploadFieldProblem } from './HandbookUploadField';
import { HandbookBlankPanel } from './HandbookBlankPanel';
import { isImportRunning } from './handbookImports';
import { fillErrorMessage } from './handbookFills';
import {
  DETAIL_GOALS,
  DETAIL_STEPS,
  DetailGoalId,
  SETUP_GOALS,
  SETUP_TOPICS,
  SetupGoalId,
  SetupStep,
  TopicState,
  emptyAnswers,
  formatTimeSaved,
  isSetupStep,
  mergePrefill,
  setupSteps,
  topicAnswer,
  topicState,
} from './handbookSetup';
import DreamMarque from '../common/DreamMarque';
import { ReactComponent as ChatSendIcon } from '../../icons/chat-send-icon.svg';
import './handbook-setup.scss';

export interface SetupPrefill {
  /** Ids of the documents the files became */
  documentIds: string[];
  results: HandbookPrefillResults;
}

export interface SetupFill {
  /** Ids of the new documents, in the order of the files given */
  newDocumentIds: string[];
  results: Record<string, HandbookPageFill | null>;
}

interface HandbookSetupModalProps {
  setup: HandbookSetup | null;
  /** The import the setup generated, once it has been started */
  generation: HandbookImport | null;
  generating: boolean;
  error: string | null;
  onSave: (patch: SaveHandbookSetupRequest) => Promise<boolean>;
  /** Uploads the files and reads answers for the goals from them; throws when that fails */
  onPrefill: (files: File[], goals: SetupGoalId[]) => Promise<SetupPrefill>;
  /** Uploads the new `files`, then writes each title from them and the `documentIds` already uploaded; throws when that fails */
  onFill: (
    files: File[],
    documentIds: string[],
    titles: string[],
  ) => Promise<SetupFill>;
  /** The organisation's document library, to name the documents answers came from */
  loadDocuments: () => Promise<HandbookDocument[]>;
  onGenerate: (answers: HandbookSetupAnswers, documentIds: string[]) => void;
  /** Suggested questions with no question, or a sample reply to one */
  onPreview: (question?: string) => Promise<HandbookPreviewResponse | null>;
  onClose: () => void;
  /** Closes the setup and shows the generated draft pages */
  onDone: () => void;
}

interface PreviewMessage {
  from: 'user' | 'assistant';
  text: string;
  sources?: HandbookPreviewSource[];
}

const STATE_LABELS: Record<TopicState, string> = {
  added: 'Added',
  missing: 'Missing',
};

const EMPTY_SECTION = (): HandbookSetupOwnSection => ({
  title: '',
  pages: [{ title: '', fill: null }],
});

function initialStep(
  setup: HandbookSetup | null,
  generation: HandbookImport | null,
): SetupStep {
  if (generation) return 'generate';
  const saved = setup?.step ?? null;
  return isSetupStep(saved) && saved !== 'generate' ? saved : 'intro';
}

const detailAnswered = (answers: HandbookSetupAnswers, goal: DetailGoalId) =>
  DETAIL_STEPS[goal].questions.some((q) => answers.details[q.id]?.trim());

const namedPages = (section: HandbookSetupOwnSection) =>
  section.title.trim() ? section.pages.filter((p) => p.title.trim()) : [];

const sourcesLabel = (sources: HandbookPreviewSource[]) =>
  sources
    .map((s) => `${s.title}${s.from === 'draft' ? ' (draft)' : ''}`)
    .join(', ');

/** Counts the shown value towards `target` in 5-minute steps. */
function useCountUp(target: number): number {
  const [shown, setShown] = useState(target);
  useEffect(() => {
    if (shown === target) return;
    const timer = setTimeout(
      () =>
        setShown((current) =>
          current < target
            ? Math.min(target, current + 5)
            : Math.max(target, current - 5),
        ),
      30,
    );
    return () => clearTimeout(timer);
  }, [shown, target]);
  return shown;
}

/**
 * Guided Handbook setup. The admin picks what the Handbook should cover, adds
 * documents first so the questions can be pre-filled from them, answers only
 * the questions for those choices, then reviews and generates draft pages
 * through the normal import. Answers save at every step, so closing pauses.
 */
export const HandbookSetupModal: FC<HandbookSetupModalProps> = ({
  setup,
  generation,
  generating,
  error,
  onSave,
  onPrefill,
  onFill,
  loadDocuments,
  onGenerate,
  onPreview,
  onClose,
  onDone,
}) => {
  const [step, setStep] = useState<SetupStep>(() =>
    initialStep(setup, generation),
  );
  const [answers, setAnswers] = useState<HandbookSetupAnswers>(
    () => setup?.answers ?? emptyAnswers(),
  );
  const [documentIds, setDocumentIds] = useState<string[]>(
    () => setup?.documentIds ?? [],
  );
  const [files, setFiles] = useState<File[]>([]);
  const [readFiles, setReadFiles] = useState<File[]>([]);
  const [documentTitles, setDocumentTitles] = useState<Record<string, string>>(
    {},
  );
  const [prefilling, setPrefilling] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [ownFiles, setOwnFiles] = useState<File[]>([]);
  const ownDocIds = useRef(new Map<File, string>());
  const lastFill = useRef<string | null>(null);
  const [filling, setFilling] = useState(false);
  const [fillError, setFillError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [chat, setChat] = useState<PreviewMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const thread = threadRef.current;
    thread?.scrollTo?.({ top: thread.scrollHeight, behavior: 'smooth' });
  }, [chat, asking]);

  useEffect(() => {
    if (generation) setStep('generate');
  }, [generation]);

  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [step]);

  const ready = generation?.status === 'ready';
  const goals = answers.goals;
  const minutesSaved = useCountUp(
    SETUP_GOALS.filter((g) => goals.includes(g.id)).reduce(
      (n, g) => n + g.minutesSaved,
      0,
    ),
  );
  const steps = setupSteps(goals);
  const questionSteps = steps.filter((s) => s !== 'intro');
  const index = steps.indexOf(step);

  const goTo = (
    nextStep: SetupStep,
    nextAnswers = answers,
    nextDocumentIds = documentIds,
  ) => {
    setAnswers(nextAnswers);
    setDocumentIds(nextDocumentIds);
    setStep(nextStep);
    onSave({
      step: nextStep,
      answers: nextAnswers,
      documentIds: nextDocumentIds,
    });
  };
  const next = () => goTo(steps[index + 1] ?? 'review');
  const back = () => goTo(steps[index - 1] ?? 'intro');
  const pause = async () => {
    if (step !== 'generate') {
      const saved = await onSave({ step, ...withoutRemovedDocuments() });
      if (!saved) return;
    }
    onClose();
  };

  const hasSavedDocuments = (setup?.documentIds.length ?? 0) > 0;
  const savedDocumentTitles = (setup?.documentIds ?? [])
    .map((id) => documentTitles[id])
    .filter(Boolean);
  useEffect(() => {
    if (!hasSavedDocuments) return;
    let cancelled = false;
    loadDocuments()
      .then((documents) => {
        if (cancelled) return;
        setDocumentTitles((current) => ({
          ...Object.fromEntries(documents.map((d) => [d.id, d.title])),
          ...current,
        }));
      })
      .catch((err) => {
        console.error('Error loading handbook documents:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [hasSavedDocuments, loadDocuments]);

  const readDocuments = async () => {
    const unread = files.filter((f) => !readFiles.includes(f));
    if (unread.length === 0) {
      next();
      return;
    }
    setPrefilling(true);
    setPrefillError(null);
    try {
      const found = await onPrefill(unread, goals);
      setReadFiles(files);
      setDocumentTitles((current) => ({
        ...current,
        ...Object.fromEntries(
          found.documentIds.map((id, i) => [id, unread[i]?.name ?? '']),
        ),
      }));
      goTo(steps[index + 1], mergePrefill(answers, found.results), [
        ...documentIds,
        ...found.documentIds,
      ]);
    } catch (err) {
      setPrefillError(
        fillErrorMessage(err, 'We couldn’t read your documents. Try again.'),
      );
    } finally {
      setPrefilling(false);
    }
  };

  const setTopic = (id: HandbookSetupTopicId, text: string) =>
    setAnswers((current) => ({
      ...current,
      topics: { ...current.topics, [id]: text },
    }));

  const setDetail = (id: string, value: string) =>
    setAnswers((current) => ({
      ...current,
      details: { ...current.details, [id]: value },
    }));

  const toggleGoal = (id: SetupGoalId) =>
    setAnswers((current) => ({
      ...current,
      goals: current.goals.includes(id)
        ? current.goals.filter((g) => g !== id)
        : SETUP_GOALS.map((g) => g.id).filter(
            (g) => g === id || current.goals.includes(g),
          ),
    }));

  const sections = answers.ownSections.length
    ? answers.ownSections
    : [EMPTY_SECTION()];
  const setSections = (nextSections: HandbookSetupOwnSection[]) =>
    setAnswers((current) => ({ ...current, ownSections: nextSections }));
  const updateSection = (i: number, patch: Partial<HandbookSetupOwnSection>) =>
    setSections(sections.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const updatePageTitle = (i: number, j: number, title: string) =>
    updateSection(i, {
      pages: sections[i].pages.map((p, k) => (k === j ? { ...p, title } : p)),
    });

  const wantsPolicies = goals.includes('policies');
  const states = SETUP_TOPICS.map((t) =>
    topicState(topicAnswer(answers, t.id)),
  );
  const chosenDetails = DETAIL_GOALS.filter((g) => goals.includes(g));
  const ownPageList = goals.includes('own') ? sections.flatMap(namedPages) : [];
  const ownTitles = Array.from(new Set(ownPageList.map((p) => p.title.trim())));
  const filledCount = ownPageList.filter((p) => p.fill).length;
  const ownFileProblem = uploadFieldProblem(ownFiles, 1);
  const knownOwnIds = ownFiles
    .filter((f) => ownDocIds.current.has(f))
    .map((f) => ownDocIds.current.get(f) as string);
  const droppedOwnIds = new Set(
    Array.from(ownDocIds.current)
      .filter(([file]) => !ownFiles.includes(file))
      .map(([, id]) => id),
  );
  const fillKey = JSON.stringify([knownOwnIds, ownTitles]);
  const needsFill =
    goals.includes('own') &&
    ownFiles.length > 0 &&
    ownTitles.length > 0 &&
    (knownOwnIds.length < ownFiles.length || lastFill.current !== fillKey);

  /** Answers and documents without anything written from a document the admin has since removed. */
  const withoutRemovedDocuments = (): {
    answers: HandbookSetupAnswers;
    documentIds: string[];
  } => {
    if (droppedOwnIds.size === 0) return { answers, documentIds };
    return {
      answers: {
        ...answers,
        ownSections: sections.map((s) => ({
          ...s,
          pages: s.pages.map((p) =>
            p.fill?.sources.some((src) => droppedOwnIds.has(src.documentId))
              ? { ...p, fill: null }
              : p,
          ),
        })),
      },
      documentIds: documentIds.filter((id) => !droppedOwnIds.has(id)),
    };
  };

  const fillOwnPages = async (): Promise<{
    answers: HandbookSetupAnswers;
    documentIds: string[];
  } | null> => {
    const fresh = ownFiles.filter((f) => !ownDocIds.current.has(f));
    setFilling(true);
    setFillError(null);
    try {
      const result = await onFill(fresh, knownOwnIds, ownTitles);
      fresh.forEach((f, i) =>
        ownDocIds.current.set(f, result.newDocumentIds[i]),
      );
      const nextAnswers: HandbookSetupAnswers = {
        ...answers,
        ownSections: sections.map((s) => ({
          ...s,
          pages: s.pages.map((p) => {
            const title = p.title.trim();
            return title ? { ...p, fill: result.results[title] ?? null } : p;
          }),
        })),
      };
      lastFill.current = JSON.stringify([
        [...knownOwnIds, ...result.newDocumentIds],
        ownTitles,
      ]);
      return {
        answers: nextAnswers,
        documentIds: [
          ...documentIds.filter((id) => !droppedOwnIds.has(id)),
          ...result.newDocumentIds,
        ],
      };
    } catch (err) {
      setFillError(
        fillErrorMessage(err, 'Could not read your documents. Try again.'),
      );
      return null;
    } finally {
      setFilling(false);
    }
  };

  const hasContent =
    (wantsPolicies && states.some((s) => s !== 'missing')) ||
    chosenDetails.some((g) => detailAnswered(answers, g)) ||
    ownPageList.length > 0;
  const canGenerate = hasContent && !generating;

  const topic = SETUP_TOPICS.find((t) => t.id === step);
  const detail = DETAIL_GOALS.find((g) => g === step);
  const title =
    step === 'intro'
      ? 'Create your Handbook'
      : topic
        ? topic.label
        : detail
          ? DETAIL_STEPS[detail].title
          : step === 'own'
            ? 'Your own information'
            : step === 'files'
              ? 'Add your documents'
              : step === 'review'
                ? 'Check and create'
                : generation?.status === 'failed'
                  ? 'We couldn’t create your Handbook'
                  : ready || generation?.status === 'published'
                    ? 'Your draft Handbook is ready'
                    : 'Creating your Handbook';

  const prefilledTag = (key: string) => {
    const source = answers.prefilled[key];
    if (!source) return null;
    const title = documentTitles[source];
    return (
      <span className='handbook-setup-prefilled'>
        From {title || 'your documents'}
      </span>
    );
  };

  const renderProgress = () => {
    const at = questionSteps.indexOf(step);
    if (at === -1) return null;
    return (
      <div className='handbook-setup-progress'>
        <span className='handbook-setup-progress-text'>
          Step {at + 1} of {questionSteps.length}
        </span>
        <div className='handbook-setup-progress-bar' aria-hidden='true'>
          {questionSteps.map((s, i) => (
            <span key={s} className={i <= at ? 'done' : ''} />
          ))}
        </div>
      </div>
    );
  };

  const renderIntro = () => (
    <div className='organisation-modal-panel handbook-setup-panel'>
      <fieldset className='organisation-modal-roles handbook-setup-goals'>
        <legend className='organisation-modal-label'>
          What do you want your Handbook to cover?
        </legend>
        <div className='handbook-setup-saving' aria-live='polite'>
          <ClockIcon aria-hidden='true' />
          <p className='handbook-setup-saving-text'>
            {minutesSaved > 0 ? (
              <>
                Save your team about{' '}
                <strong>{formatTimeSaved(minutesSaved)} a week</strong> not
                answering common questions.
              </>
            ) : (
              'See how much time you could save by selecting an option.'
            )}
          </p>
        </div>

        {SETUP_GOALS.map((g) => {
          const chosen = goals.includes(g.id);
          return (
            <label
              key={g.id}
              className={`organisation-modal-role${chosen ? ' selected' : ''}`}
            >
              <input
                type='checkbox'
                name='handbook-setup-goals'
                value={g.id}
                checked={chosen}
                onChange={() => toggleGoal(g.id)}
              />
              <span
                className='organisation-modal-radio handbook-setup-checkbox'
                aria-hidden='true'
              />
              <strong>{g.label}</strong>
              <span>{g.hint}</span>
            </label>
          );
        })}
      </fieldset>
      <p className='organisation-modal-hint'>
        Your answers save as you go, so you can stop and come back any time.
      </p>
    </div>
  );

  const renderFiles = () => (
    <div className='organisation-modal-panel handbook-setup-panel'>
      <p className='handbook-setup-lead'>
        Got an existing handbook, policies or company documents? Add them first
        and we’ll fill in as many of the next questions as we can from them.
        Using Notion? Export your pages and add the files here.
      </p>
      <HandbookUploadField
        files={files}
        onChange={setFiles}
        disabled={prefilling}
        label='Documents (optional)'
        reservedSlots={1}
      />
      {savedDocumentTitles.length > 0 && (
        <p className='organisation-modal-hint'>
          Already read: {savedDocumentTitles.join(', ')}
        </p>
      )}
      {prefilling ? (
        <p className='organisation-modal-hint' role='status'>
          Reading your documents… This can take a minute for long files.
        </p>
      ) : (
        <p className='organisation-modal-hint'>
          No documents? Skip this and answer the questions yourself.
        </p>
      )}
      {prefillError && (
        <p className='organisation-modal-hint error' role='alert'>
          {prefillError}
        </p>
      )}
    </div>
  );

  const renderTopic = (id: HandbookSetupTopicId) => {
    const t = SETUP_TOPICS.find((x) => x.id === id)!;
    const notesId = `handbook-setup-${id}-notes`;
    return (
      <div className='organisation-modal-panel handbook-setup-panel'>
        <div className='organisation-modal-field'>
          <div className='handbook-setup-field-head'>
            <label className='organisation-modal-label' htmlFor={notesId}>
              {t.question}
            </label>
            {prefilledTag(id)}
          </div>
          <p className='organisation-modal-hint'>
            {answers.prefilled[id]
              ? 'We found this in your documents. Check it’s right and up to date.'
              : 'Be specific: numbers, deadlines, who to contact and where things are done.'}
          </p>
          <textarea
            id={notesId}
            className='organisation-modal-input handbook-setup-textarea'
            value={topicAnswer(answers, id)}
            maxLength={4000}
            placeholder={t.placeholder}
            onChange={(e) => setTopic(id, e.target.value)}
          />
        </div>
        <button
          type='button'
          className='handbook-setup-link'
          onClick={() => goTo(steps[index + 1])}
        >
          Skip for now
        </button>
      </div>
    );
  };

  const renderDetail = (goal: DetailGoalId) => (
    <div className='organisation-modal-panel handbook-setup-panel'>
      {DETAIL_STEPS[goal].questions.map((q) => {
        const fieldId = `handbook-setup-${q.id.replace('.', '-')}`;
        return (
          <div className='organisation-modal-field' key={q.id}>
            <div className='handbook-setup-field-head'>
              <label className='organisation-modal-label' htmlFor={fieldId}>
                {q.label}
              </label>
              {prefilledTag(q.id)}
            </div>
            <textarea
              id={fieldId}
              className='organisation-modal-input handbook-setup-textarea short'
              value={answers.details[q.id] ?? ''}
              maxLength={4000}
              placeholder={q.placeholder}
              onChange={(e) => setDetail(q.id, e.target.value)}
            />
          </div>
        );
      })}
      <p className='organisation-modal-hint'>
        Leave anything you’re not sure about blank. You can add it later.
      </p>
    </div>
  );

  const renderOwn = () => (
    <div className='organisation-modal-panel handbook-setup-panel'>
      <p className='handbook-setup-lead'>
        Add a section and the pages you want in it. Add documents too if you
        have them: when you press Next we write only those pages from them and
        leave out anything else.
      </p>
      <div className='handbook-setup-sections'>
        {sections.map((section, i) => (
          <div className='handbook-setup-section' key={i}>
            <div className='organisation-modal-field'>
              <label
                className='organisation-modal-label'
                htmlFor={`handbook-setup-section-${i}`}
              >
                Section
              </label>
              <input
                id={`handbook-setup-section-${i}`}
                className='organisation-modal-input'
                value={section.title}
                placeholder='e.g. Client work'
                onChange={(e) => updateSection(i, { title: e.target.value })}
              />
            </div>
            <div className='handbook-setup-section-pages'>
              {section.pages.map((page, j) => (
                <div className='handbook-setup-section-page' key={j}>
                  <input
                    aria-label={`Page ${j + 1} in section ${i + 1}`}
                    className='organisation-modal-input'
                    value={page.title}
                    placeholder='Page title, e.g. Writing a proposal'
                    onChange={(e) => updatePageTitle(i, j, e.target.value)}
                  />
                  {section.pages.length > 1 && (
                    <button
                      type='button'
                      className='handbook-setup-link'
                      onClick={() =>
                        updateSection(i, {
                          pages: section.pages.filter((_, k) => k !== j),
                        })
                      }
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <div className='handbook-setup-section-actions'>
                <button
                  type='button'
                  className='handbook-setup-link'
                  onClick={() =>
                    updateSection(i, {
                      pages: [...section.pages, { title: '', fill: null }],
                    })
                  }
                >
                  Add a page
                </button>
                {sections.length > 1 && (
                  <button
                    type='button'
                    className='handbook-setup-link'
                    onClick={() =>
                      setSections(sections.filter((_, k) => k !== i))
                    }
                  >
                    Remove section
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type='button'
        className='handbook-pill handbook-setup-add-section'
        onClick={() => setSections([...sections, EMPTY_SECTION()])}
      >
        Add a section
      </button>
      <HandbookUploadField
        files={ownFiles}
        onChange={setOwnFiles}
        disabled={filling}
        label='Documents to fill your pages from (optional)'
        reservedSlots={1}
      />
      {filling && (
        <p className='organisation-modal-hint' role='status'>
          Writing your pages from the documents… This can take a minute or two.
        </p>
      )}
      {ownFileProblem && (
        <p className='organisation-modal-hint handbook-setup-warning'>
          {ownFileProblem}
        </p>
      )}
      {fillError && (
        <p className='organisation-modal-hint error' role='alert'>
          {fillError}
        </p>
      )}
    </div>
  );

  const reviewRow = (
    key: string,
    label: string,
    target: SetupStep,
    state: string,
    text: string,
  ) => (
    <li key={key}>
      <button
        type='button'
        className='handbook-setup-review-topic'
        onClick={() => goTo(target)}
      >
        {label}
      </button>
      <span className={`handbook-setup-state ${state}`}>{text}</span>
    </li>
  );

  const countLabel = (n: number, one: string, many: string) =>
    `${n} ${n === 1 ? one : many}`;

  const renderReview = () => {
    const missing =
      (wantsPolicies && states.includes('missing')) ||
      chosenDetails.some((g) => !detailAnswered(answers, g));
    return (
      <div className='organisation-modal-panel handbook-setup-panel'>
        <ul className='handbook-setup-review' aria-label='Your answers'>
          {steps.includes('files') &&
            reviewRow(
              'files',
              'Documents',
              'files',
              'neutral',
              documentIds.length === 0
                ? 'None'
                : countLabel(documentIds.length, 'document', 'documents'),
            )}
          {wantsPolicies &&
            SETUP_TOPICS.map((t, i) =>
              reviewRow(
                t.id,
                t.label,
                t.id,
                states[i],
                STATE_LABELS[states[i]],
              ),
            )}
          {chosenDetails.map((g) => {
            const answered = detailAnswered(answers, g);
            return reviewRow(
              g,
              DETAIL_STEPS[g].title,
              g,
              answered ? 'added' : 'missing',
              answered ? 'Added' : 'Missing',
            );
          })}
          {goals.includes('own') &&
            reviewRow(
              'own',
              'Your own information',
              'own',
              'neutral',
              [
                countLabel(ownPageList.length, 'page', 'pages'),
                filledCount ? `${filledCount} filled from documents` : null,
              ]
                .filter(Boolean)
                .join(', '),
            )}
        </ul>
        {missing && (
          <p className='organisation-modal-hint'>
            Missing areas are flagged on your Handbook so you can fill them in
            later.
          </p>
        )}
        {!hasContent && (
          <p className='organisation-modal-hint handbook-setup-warning'>
            Answer at least one question or add a page to create your Handbook.
          </p>
        )}
        <p className='handbook-setup-lead'>
          Your Handbook is created as draft pages. Nothing goes live until you
          publish it.
        </p>
      </div>
    );
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    if (suggestions.length) return;
    const res = await onPreview();
    setSuggestions(res?.suggestions ?? []);
  };

  const ask = async (text: string) => {
    const q = text.trim();
    if (!q || asking) return;
    setChat((c) => [...c, { from: 'user', text: q }]);
    setQuestion('');
    setAsking(true);
    const res = await onPreview(q);
    setChat((c) => [
      ...c,
      {
        from: 'assistant',
        text: res?.answer ?? 'Something went wrong. Try asking again.',
        sources: res?.sources,
      },
    ]);
    setAsking(false);
  };

  const renderPreview = () => {
    const unused = suggestions.filter((s) => !chat.some((m) => m.text === s));
    return (
      <div className='handbook-pa-test'>
        <p className='handbook-setup-lead'>
          Test your handbook by asking questions and see how a secretary would
          reply
        </p>
        <div className='handbook-preview-panel'>
          <div
            className='handbook-preview-thread'
            ref={threadRef}
            aria-live='polite'
          >
            {chat.map((m, i) => (
              <div key={i} className={`handbook-preview-message ${m.from}`}>
                {m.from === 'assistant' && (
                  <span className='handbook-preview-avatar' aria-hidden='true'>
                    <DreamMarque variant='optical-md' />
                  </span>
                )}
                <div className='handbook-preview-bubble'>
                  <p>{m.text}</p>
                  {!!m.sources?.length && (
                    <span className='handbook-preview-source'>
                      From {sourcesLabel(m.sources)}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {asking && (
              <div className='handbook-preview-message assistant'>
                <span className='handbook-preview-avatar' aria-hidden='true'>
                  <DreamMarque variant='optical-md' />
                </span>
                <span
                  className='handbook-preview-typing'
                  role='status'
                  aria-label='Writing a reply'
                >
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            )}
          </div>
          {unused.length > 0 && (
            <div className='handbook-preview-suggestions'>
              {unused.map((s) => (
                <button
                  key={s}
                  type='button'
                  className='handbook-preview-chip'
                  disabled={asking}
                  onClick={() => ask(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className='handbook-preview-input'>
            <textarea
              rows={1}
              aria-label='Ask your own question'
              placeholder='Ask your own question'
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask(question);
                }
              }}
            />
            <button
              type='button'
              className='handbook-preview-send'
              aria-label='Send'
              disabled={!question.trim() || asking}
              onClick={() => ask(question)}
            >
              <ChatSendIcon aria-hidden='true' />
            </button>
          </div>
        </div>
        <p className='handbook-preview-note'>
          Secretaries can use other sources of information when choosing how to
          reply.
        </p>
      </div>
    );
  };

  const renderGenerate = () => {
    if (!generation || isImportRunning(generation.status)) {
      return (
        <div className='organisation-modal-panel handbook-setup-panel handbook-setup-working'>
          <span className='organisation-barrier-spinner' aria-hidden='true' />
          <p className='handbook-setup-lead' role='status'>
            Writing your draft pages. This usually takes a minute or two.
          </p>
          <p className='organisation-modal-hint'>
            You can close this and come back. It keeps going in the background.
          </p>
        </div>
      );
    }
    if (generation.status === 'failed') {
      return (
        <div className='organisation-modal-panel handbook-setup-panel'>
          <p className='handbook-setup-lead'>
            {generation.error ?? 'Something went wrong reading your answers.'}
          </p>
          <p className='organisation-modal-hint'>
            Your answers are saved. Go back to try again.
          </p>
        </div>
      );
    }
    if (previewOpen) return renderPreview();
    const count = generation.pageSlugs.length;
    return (
      <div className='handbook-setup-result'>
        <p className='handbook-setup-lead'>
          We’ve written {count} draft {count === 1 ? 'page' : 'pages'}. None of
          them are live yet: review them, then publish when you’re happy.
        </p>
        {ready && (
          <section className='handbook-preview-entry'>
            <ChatIcon
              className='handbook-preview-entry-icon'
              aria-hidden='true'
            />
            <div className='handbook-preview-entry-text'>
              <strong>
                See what responses from your secretary would look like
              </strong>
              <span>
                Ask a few questions and see how your secretary answers from
                these pages.
              </span>
            </div>
            <button
              type='button'
              className='organisation-modal-cancel handbook-preview-entry-cta'
              onClick={openPreview}
            >
              Try it
            </button>
          </section>
        )}
        <HandbookBlankPanel answers={answers} />
      </div>
    );
  };

  const nextLabel =
    step === 'intro'
      ? 'Get started'
      : step === 'review'
        ? 'Create Handbook'
        : step === 'files' && files.length === 0
          ? 'Skip'
          : step === 'own' && filling
            ? 'Filling your pages…'
            : 'Next';
  const failed = generation?.status === 'failed';
  const working = generating || prefilling || filling;
  const submitDisabled =
    step === 'review'
      ? !canGenerate
      : step === 'intro'
        ? goals.length === 0
        : step === 'files'
          ? working || !!uploadFieldProblem(files, 1)
          : step === 'own'
            ? working || !!ownFileProblem
            : working;

  const actions =
    step === 'generate' && previewOpen ? (
      <>
        <button
          type='button'
          className='organisation-modal-cancel'
          onClick={() => setPreviewOpen(false)}
        >
          Back
        </button>
        <button
          type='button'
          className='organisation-modal-primary handbook-setup-finish'
          onClick={onDone}
        >
          Review and finish
          <ArrowRightIcon aria-hidden='true' />
        </button>
      </>
    ) : step === 'generate' ? (
      <>
        {failed ? (
          <button
            type='button'
            className='organisation-modal-cancel'
            onClick={() => goTo('review')}
          >
            Back
          </button>
        ) : (
          <span className='handbook-setup-actions-spacer' />
        )}
        {ready || generation?.status === 'published' ? (
          <button
            type='button'
            className='organisation-modal-primary handbook-setup-finish'
            onClick={onDone}
          >
            Review and finish
            <ArrowRightIcon aria-hidden='true' />
          </button>
        ) : (
          <button
            type='button'
            className='organisation-modal-primary'
            onClick={onClose}
          >
            Close
          </button>
        )}
      </>
    ) : (
      <>
        <button
          type='button'
          className='organisation-modal-cancel'
          disabled={working}
          onClick={step === 'intro' ? onClose : back}
        >
          {step === 'intro' ? 'Not now' : 'Back'}
        </button>
        <div className='handbook-setup-actions-end'>
          {step !== 'intro' && (
            <button
              type='button'
              className='handbook-setup-link'
              disabled={working}
              onClick={pause}
            >
              Save and finish later
            </button>
          )}
          <button
            type='submit'
            className='organisation-modal-primary organisation-barrier-submit'
            disabled={submitDisabled}
            aria-busy={working}
          >
            {working && (
              <span
                className='organisation-barrier-spinner'
                aria-hidden='true'
              />
            )}
            {nextLabel}
          </button>
        </div>
      </>
    );

  return (
    <OrganisationModal
      icon={<BookOpenIcon />}
      title={title}
      error={error}
      busy={working}
      testId='handbook-setup-modal'
      className='handbook-setup-modal'
      onClose={pause}
      onSubmit={() => {
        if (step === 'generate') return;
        if (step === 'review') {
          if (canGenerate) onGenerate(answers, documentIds);
          return;
        }
        if (step === 'files') {
          readDocuments();
          return;
        }
        if (step === 'own') {
          if (needsFill) {
            fillOwnPages().then((filled) => {
              if (filled) {
                goTo(
                  steps[index + 1] ?? 'review',
                  filled.answers,
                  filled.documentIds,
                );
              }
            });
          } else {
            const pruned = withoutRemovedDocuments();
            goTo(
              steps[index + 1] ?? 'review',
              pruned.answers,
              pruned.documentIds,
            );
          }
          return;
        }
        next();
      }}
      actions={actions}
    >
      {renderProgress()}
      <div className='handbook-setup-body' ref={bodyRef}>
        {step === 'intro' && renderIntro()}
        {step === 'files' && renderFiles()}
        {topic && renderTopic(topic.id)}
        {detail && renderDetail(detail)}
        {step === 'own' && renderOwn()}
        {step === 'review' && renderReview()}
        {step === 'generate' && renderGenerate()}
      </div>
    </OrganisationModal>
  );
};
